// hooks/useDeliveryOrder.js
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getAllOrders,
    getOrder,
    getOrderStats,
    removeOrder,
    removeOrders,
    softDeleteOrder as softDeleteOrderRequest,
    updateOrder,
} from "services/order";
import { setOrders, setOrderStats, updateOrderInList } from "store/delvieryOrderSlice";
import { useSearchParams } from "react-router-dom";

/**
 * Order data hook for the DRIVER-audience screen (/deliveryOrders). A
 * near-verbatim copy of hooks/useOrder against a second Redux slice.
 *
 * "Delivery" here means the delivery-DRIVER audience, NOT type="delivery"
 * orders: this hook calls the same `getAllOrders` endpoint with no type filter,
 * so it returns the same normal + delivery + whatsapp dataset that /orders
 * shows. The screens are two views of one table, not two datasets.
 *
 * THE THREE DIFFERENCES from useOrder — the only ones:
 * 1. Redux slice: `state.deliveryOrder` via store/delvieryOrderSlice (note the
 *    misspelled module name) instead of `state.order`. This separation is the
 *    whole reason the hook is duplicated: each screen needs its own page,
 *    filter and aggregate state so the two do not clobber each other.
 * 2. Default soft-delete filter: reads the `deletedForDelivery` URL param and
 *    defaults to `deletedForDelivery=false`, where useOrder reads
 *    `deletedForVendor` and defaults to `deletedForVendor=false`. This single
 *    filter is what makes the same rows "already settled" on one screen and
 *    still visible on the other.
 * 3. No creators: `createQuickOrder` is absent because
 *    order entry lives only on /orders. Everything else — fetch, fetch-by-id,
 *    optimistic edit, hard delete, bulk soft delete, single soft delete — is
 *    identical code.
 *
 * Consequence of the duplication: any fix to the optimistic-update, rollback or
 * error-reporting behaviour must be applied to BOTH files or the two screens
 * drift apart. The same params-derivation duplication described in useOrder
 * applies here against components/DeliveryOrders/Order.js.
 */
const useDeliveryOrder = () => {
    const orders = useSelector((state) => state.deliveryOrder.orders);
    const page = useSelector((state) => state.deliveryOrder.page);
    const adminInfo = useSelector((state) => state.auth.userData);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const dispatch = useDispatch();

    const [params, setParams] = useState({});
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const newParams = {};
        newParams.page = page;
        newParams.size = 12;

        if (searchParams.get("vendor")) {
            newParams.vendorId = searchParams.get("vendor");
        }
        if (searchParams.get("delivery")) {
            newParams.deliveryId = searchParams.get("delivery");
        }
        if (searchParams.get("status")) {
            newParams.status = searchParams.get("status");
        }
        if (searchParams.get("startDate")) {
            newParams.startDate = searchParams.get("startDate");
        }
        if (searchParams.get("endDate")) {
            newParams.endDate = searchParams.get("endDate");
        }
        if (searchParams.get("search")) {
            newParams.search = searchParams.get("search");
        }
        const selectedCity = searchParams.get("city");
        const resolvedCityId = adminInfo?.super_admin ? selectedCity : adminInfo?.cityId;

        if (resolvedCityId) {
            newParams.cityId = resolvedCityId;
        }
        // "الطلبات المحذوفة" filter — the driver-audience counterpart of
        // useOrder's `deletedForVendor`. Default: hide orders already settled
        // with the driver. "all" → show both. "true" → only settled ones.
        const deletedFilter = searchParams.get("deletedForDelivery");
        if (deletedFilter === "all") {
            // Show deleted too: don't filter
        } else if (deletedFilter === "true") {
            newParams.deletedForDelivery = "true";
        } else {
            newParams.deletedForDelivery = "false";
        }
        setParams(newParams);
    }, [adminInfo?.cityId, adminInfo?.super_admin, page, searchParams]);

    /**
     * Loads delivery settlement aggregates separately from the rows. Billable
     * vendor-statement metrics are deliberately disabled on this screen.
     *
     * @param {object} requestParams - Current order filters.
     * @returns {Promise<object|undefined>}
     */
    const fetchOrderStats = useCallback(
        async (requestParams) => {
            try {
                const data = await getOrderStats(requestParams, false);
                dispatch(setOrderStats(data));
                return data;
            } catch (err) {
                setError(err.message || "Failed to fetch order stats");
            }
        },
        [dispatch],
    );

    // Fetch Order
    const fetchOrders = useCallback(
        async (params) => {
            setLoading(true);
            try {
                const data = await getAllOrders(params);

                dispatch(setOrders(data));
                return data;
            } catch (err) {
                setError(err.message || "Failed to fetch orders");
            } finally {
                setLoading(false);
            }
        },
        [dispatch],
    );

    const refetchOrders = useCallback(
        async (params) => {
            fetchOrderStats(params);
            try {
                const data = await getAllOrders({
                    ...params,
                });

                dispatch(setOrders(data));
                return data;
            } catch (err) {
                setError(err.message || "Failed to fetch orders");
            } finally {
                // no cleanup needed
            }
        },
        [dispatch, fetchOrderStats],
    );
    // Fetch Order
    const fetchOrderBYId = async (id) => {
        setLoading(true);
        try {
            const data = await getOrder(id);

            return data.order;
        } catch (err) {
            setError(err.message || "Failed to fetch products");
        } finally {
            setLoading(false);
        }
    };

    /**
     * Identical to useOrder#editOrder — see that file for the full contract.
     * Summary: optimistic Redux patch, refetch-based rollback, failures
     * returned rather than thrown, and a plain object (not FormData) is
     * required to send a null field.
     *
     * @param {number|string} id - Order id.
     * @param {FormData|object} updatedData - Partial order fields.
     */
    const editOrder = async (id, updatedData) => {
        // Extract plain object from FormData or use as-is
        const data = {};
        if (updatedData instanceof FormData) {
            updatedData.forEach((value, key) => {
                data[key] = value;
            });
        } else {
            Object.assign(data, updatedData);
        }

        // Optimistically update the order in Redux immediately
        dispatch(updateOrderInList({ id, data }));

        try {
            const updated = await updateOrder(id, updatedData);

            // Silent background refetch to sync with server (no loading spinner)
            refetchOrders({ ...params });

            return updated;
        } catch (err) {
            // Rollback: refetch the real data from server
            refetchOrders({ ...params });
            setError(err.response?.data?.message || "Failed to edit order");
            return err;
        }
    };

    // Delete Order
    const deleteOrder = async (id) => {
        setLoading(true);
        try {
            await removeOrder(id);

            refetchOrders({
                ...params,
            });

            return true;
        } catch (err) {
            setError(err.message || "Failed to delete Order");
            return err;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Bulk SOFT delete for the driver audience — the "حذف طلبات السائقين"
     * button, i.e. the last step of a driver settlement: once the operator has
     * paid the driver for the filtered window, those orders are hidden from the
     * driver ledger so the next window starts clean. Nothing else records that
     * the settlement happened — the hidden flag IS the record.
     *
     * CONSTRAINT: scope is entirely whatever `deleteParams` carries, and every
     * narrowing filter is optional server-side. The page only exposes the
     * button while a driver filter is active and spreads its live params in;
     * calling this with just `deleteFor` would hide every order.
     *
     * @param {object} deleteParams - The current list filters plus
     *   `deleteFor: "vendor"|"delivery"`.
     */
    const deleteOrders = async (deleteParams) => {
        setLoading(true);
        try {
            const data = await removeOrders({
                ...deleteParams,
            });

            refetchOrders({
                ...params,
            });

            // api#167: the sweep skips running orders and says how many.
            return { ok: true, updated: data?.updated, skippedRunning: data?.skippedRunning ?? 0 };
        } catch (err) {
            setError(err.message || "Failed to delete Order");
            return { ok: false, message: err.response?.data?.message };
        } finally {
            setLoading(false);
        }
    };

    /**
     * Soft-deletes one order for either vendor or delivery visibility and refreshes current listing filters.
     *
     * @param {number|string} id - Order id.
     * @param {"vendor"|"delivery"} deleteFor - Target audience visibility to remove.
     */
    const softDeleteOrder = async (id, deleteFor) => {
        setLoading(true);
        try {
            await softDeleteOrderRequest(id, deleteFor);

            refetchOrders({
                ...params,
            });

            return { ok: true };
        } catch (err) {
            setError(err.response?.data?.message || "Failed to soft delete order");
            // api#167: a running order answers 409 with the reason to show.
            return { ok: false, message: err.response?.data?.message };
        } finally {
            setLoading(false);
        }
    };
    return {
        orders,
        loading,
        error,
        fetchOrders,
        fetchOrderStats,
        fetchOrderBYId,
        deleteOrder,
        editOrder,
        deleteOrders,
        softDeleteOrder,
        refetchOrders,
    };
};

export default useDeliveryOrder;
